import { useEffect, useState } from "react";

import {
  ArrowButton,
  IconWrapper,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { ButtonLink } from "@king-ict/eupisi-ui/components/ButtonLink/ButtonLink";
import { ReactComponent as ArchiveDrawerIcon } from "@king-ict/eupisi-ui/components/Icon/ArchiveDrawer.svg";
import { ReactComponent as ControlDownIcon } from "@king-ict/eupisi-ui/components/Icon/ControlDown.svg";
import { useTranslation } from "react-i18next";

import {
  getEnrollmentCyclesForPedagogicalYear,
} from "../../api/kindergartenApi";
import {
  useGetRequestCountsPerChildQuery,
  useGetRequestsQuery,
} from "../../api/requestApi";
import { useTypedSelector } from "../../app/rootReducer";
import { useAppDispatch } from "../../app/store";
import { useCurrentUser } from "../../hooks/authorization/useCurrentUser";
import { useGetMonths } from "../../hooks/codebook/useGetMonths";
import { EnrollmentCyclePedagogicalYear } from "../../models/EnrollmentCycle/EnrollmentCyclePedagogicalYear";
import { webUserPortalAplicationUrl } from "../../routes/routes";
import { getChildren } from "../../slices/personSlice";
import { clearRequestData } from "../../slices/requestSlice";
import DashboardList from "./DashboardList";
import styles from "./DashboardPage.module.scss";

export default function DashboardPage() {
  const [showArchive, setShowArchive] = useState(false);

  const { t } = useTranslation();

  const user = useCurrentUser();
  const currentPedagogicalYear = useTypedSelector(
    (state) => state.pedagogicalYear.currentPedagogicalYear,
  );
  const currentImportantEvent = useTypedSelector(
    (state) => state.pedagogicalYear.currentImportantDate,
  );

  const children = useTypedSelector((state) => state.person.children);
  const { data: requests = [] } = useGetRequestsQuery(undefined, {
    skip: !user.oib,
  });
  const { data: childrenRequestsCount = [] } =
    useGetRequestCountsPerChildQuery(undefined, {
      skip: !user.oib,
    });

  const months = useGetMonths();

  const [
    enrollmentCyclesForPedagogicalYear,
    setEnrollmentCyclesForPedagogicalYear,
  ] = useState<EnrollmentCyclePedagogicalYear[]>([]);

  useEffect(() => {
    const getEnrollmentCyclesHelper = async () => {
      if (currentPedagogicalYear.id) {
        var results = await getEnrollmentCyclesForPedagogicalYear(
          currentPedagogicalYear.id,
        );
        setEnrollmentCyclesForPedagogicalYear(results);
      }
    };
    getEnrollmentCyclesHelper();
  }, [currentPedagogicalYear.id]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (user.oib) {
      dispatch(clearRequestData());
      dispatch(getChildren());
    }
  }, [dispatch, user.oib]);

  const pedagogicalYears = [
    currentPedagogicalYear,
    ...Array.from(
      new Set(
        requests
          .map((r) => r.pedagogicalYear)
          .filter(
            (pedagogicalYear, index, pedagogicalYears) =>
              !pedagogicalYear.isActive &&
              pedagogicalYears.findIndex(
                (py) => py.id === pedagogicalYear.id,
              ) === index,
          ),
      ),
    ),
  ].sort((a, b) => b.dateFrom.getTime() - a.dateFrom.getTime());

  const getPedagogicalYearChildren = (pedagogicalYearId: number) => {
    const childArray = [
      ...(pedagogicalYearId === currentPedagogicalYear.id ? [...children] : []),
    ];

    requests
      .filter((r) => r.pedagogicalYear.id === pedagogicalYearId)
      .forEach((cd) => {
        if (childArray.every((x) => x.oib !== cd.child.oib)) {
          childArray.push(cd.child);
        }
      });

    return childArray.sort(function (a, b) {
      return a.firstName.localeCompare(b.firstName);
    });
  };

  const getPedagogicalYearRequests = (pedagogicalYearId: number) => {
    return requests.filter((r) => r.pedagogicalYear.id === pedagogicalYearId);
  };

  const archiveExists = pedagogicalYears.some((py) => !py.isActive);

  const CurrentYearDashboardList = () => {
    const currentYear = pedagogicalYears.find((py) => py.isActive);

    return (
      <>
        {currentYear && (
          <DashboardList
            pedagogicalYear={currentYear}
            pedagogicalYearChildren={getPedagogicalYearChildren(currentYear.id)}
            pedagogicalYearRequests={getPedagogicalYearRequests(currentYear.id)}
            currentImportantDate={currentImportantEvent.dateTimeTo}
            childrenRequestsCount={childrenRequestsCount}
            months={months}
            enrollmentCyclesForPedagogicalYear={
              enrollmentCyclesForPedagogicalYear
            }
          />
        )}
      </>
    );
  };

  const handleBackToEUpisi = () => {
    window.location.href = `${webUserPortalAplicationUrl}`;
  };

  return (
    <>
      <div className={styles.buttonContent}>
        <ArrowButton
          ariaLabel={t("accessibility.backButton")}
          backwards
          onClick={handleBackToEUpisi}
        ></ArrowButton>
        <Typography
          variant="base"
          color="primary"
          className={styles.buttonText}
        >
          {t("dashboard.backToEUpisi")}
        </Typography>
      </div>
      <CurrentYearDashboardList />
      {archiveExists && (
        <>
          <ButtonLink
            className={styles.archiveButton}
            onClick={() => setShowArchive((prev) => !prev)}
          >
            <div className={styles.archiveButtonContentContainer}>
              <IconWrapper color="primary">
                <ArchiveDrawerIcon />
              </IconWrapper>
              <span className={styles.archiveButtonText}>
                {t("dashboard.loadArchivedRequests")}
              </span>
              <IconWrapper color="primary">
                <ControlDownIcon />
              </IconWrapper>
            </div>
          </ButtonLink>
          {showArchive &&
            pedagogicalYears
              .filter((py) => !py.isActive)
              .map((py) => (
                <DashboardList
                  key={py.id}
                  pedagogicalYear={py}
                  pedagogicalYearChildren={getPedagogicalYearChildren(py.id)}
                  pedagogicalYearRequests={getPedagogicalYearRequests(py.id)}
                  months={months}
                  enrollmentCyclesForPedagogicalYear={
                    enrollmentCyclesForPedagogicalYear
                  }
                />
              ))}
        </>
      )}
    </>
  );
}
